import React, { useState } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { teamMembers, consultationBenefits, consultationImages } from '../data/mock';
import { Phone, Mail, Calendar, Video, MapPin, Clock, ArrowRight, CheckCircle2, Users, HeartHandshake, Target, Sparkles } from 'lucide-react';
import ChatWidget from '../components/common/ChatWidget';
import CallbackModal from '../components/common/CallbackModal';
import BookingModal from '../components/common/BookingModal';

const ConsultationPage = () => {
  const [isCallbackModalOpen, setIsCallbackModalOpen] = useState(false);
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Callback Modal */}
      <CallbackModal 
        isOpen={isCallbackModalOpen} 
        onClose={() => setIsCallbackModalOpen(false)} 
      />
      
      {/* Booking Modal */}
      <BookingModal 
        isOpen={isBookingModalOpen} 
        onClose={() => setIsBookingModalOpen(false)} 
      />
      
      <main>
        {/* Hero Section */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-slate-50" />
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <span className="inline-block text-blue-600 font-semibold text-sm uppercase tracking-wider mb-4">
                  Persönliche Beratung
                </span>
                <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
                  Kompetente Beratung für Ihre Finanzierung
                </h1>
                <p className="text-lg text-slate-600 mb-8">
                  Unsere erfahrenen Berater stehen Ihnen zur Seite – persönlich, per Telefon oder Video. Profitieren Sie von unserer Expertise und finden Sie die optimale Finanzierungslösung.
                </p>
                
                <div className="flex flex-wrap gap-4">
                  <Button 
                    size="lg" 
                    className="bg-blue-600 hover:bg-blue-700 text-white shadow-lg"
                    onClick={() => setIsBookingModalOpen(true)}
                  >
                    Termin buchen
                    <Calendar className="w-5 h-5 ml-2" />
                  </Button>
                  <Button 
                    size="lg" 
                    variant="outline" 
                    className="border-slate-300"
                    onClick={() => setIsCallbackModalOpen(true)}
                  >
                    Rückruf anfordern
                    <Phone className="w-5 h-5 ml-2" />
                  </Button>
                </div>
              </div>

              <div className="relative">
                <div className="rounded-2xl overflow-hidden shadow-2xl">
                  <img
                    src={consultationImages.hero}
                    alt="Beratungsgespräch"
                    className="w-full h-96 object-cover"
                  />
                </div>
                
                {/* Floating Card */}
                <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-xl p-4 max-w-xs">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                      <CheckCircle2 className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">98% Zufriedenheit</p>
                      <p className="text-sm text-slate-500">über 10.000 Beratungen</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Consultation Process */}
        <section className="py-20 bg-slate-50">
          <div className="container mx-auto px-4">
            <div className="text-center max-w-2xl mx-auto mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Der Beratungsprozess
              </h2>
              <p className="text-slate-600">
                Von der ersten Kontaktaufnahme bis zur Finanzierungszusage – wir begleiten Sie.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {[
                {
                  step: 1,
                  icon: Phone,
                  title: 'Erstkontakt',
                  description: 'Sie kontaktieren uns telefonisch, per E-Mail oder über unser Online-Formular.'
                },
                {
                  step: 2,
                  icon: Target,
                  title: 'Bedarfsanalyse',
                  description: 'Wir analysieren Ihre Situation und Ihre Finanzierungswünsche im Detail.'
                },
                {
                  step: 3,
                  icon: Sparkles,
                  title: 'Angebotserstellung',
                  description: 'Sie erhalten maßgeschneiderte Angebote von unseren Partnerbanken.'
                },
                {
                  step: 4,
                  icon: HeartHandshake,
                  title: 'Abschluss',
                  description: 'Wir begleiten Sie bis zur Vertragsunterschrift und Auszahlung.'
                }
              ].map((item) => (
                <div key={item.step} className="relative">
                  <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-100 h-full">
                    <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-sm mb-4">
                      {item.step}
                    </div>
                    <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center mb-4">
                      <item.icon className="w-6 h-6 text-blue-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">{item.title}</h3>
                    <p className="text-slate-600 text-sm">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <span className="text-blue-600 font-semibold text-sm uppercase tracking-wider">Vorteile</span>
                <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mt-3 mb-8">
                  Warum persönliche Beratung?
                </h2>
                
                <div className="space-y-6">
                  {consultationBenefits.map((benefit) => (
                    <div key={benefit.id} className="flex gap-4">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <CheckCircle2 className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900 mb-1">{benefit.title}</h3>
                        <p className="text-slate-600 text-sm">{benefit.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <img
                  src={consultationImages.meeting}
                  alt="Beratungsmeeting"
                  className="rounded-2xl shadow-lg w-full h-64 object-cover"
                />
                <img
                  src={consultationImages.consultation}
                  alt="Persönliche Beratung"
                  className="rounded-2xl shadow-lg w-full h-64 object-cover mt-8"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Team Section */}
        <section className="py-20 bg-slate-50">
          <div className="container mx-auto px-4">
            <div className="text-center max-w-2xl mx-auto mb-16">
              <span className="text-blue-600 font-semibold text-sm uppercase tracking-wider">Unser Team</span>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mt-3 mb-4">
                Ihre Ansprechpartner
              </h2>
              <p className="text-slate-600">
                Erfahrene Finanzexperten mit langjähriger Branchenerfahrung.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {teamMembers.map((member) => (
                <div key={member.id} className="bg-white rounded-2xl overflow-hidden shadow-lg border border-slate-100 group">
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src={member.image}
                      alt={member.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-semibold text-slate-900">{member.name}</h3>
                    <p className="text-blue-600 font-medium text-sm mb-2">{member.role}</p>
                    <p className="text-slate-600 text-sm">{member.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Consultation Options */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="text-center max-w-2xl mx-auto mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Beratung ganz nach Ihren Wünschen
              </h2>
              <p className="text-slate-600">
                Wählen Sie die Beratungsform, die am besten zu Ihnen passt.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-slate-50 rounded-2xl p-8 border border-slate-100 hover:shadow-lg transition-shadow text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-6">
                  <Phone className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-3">Telefonberatung</h3>
                <p className="text-slate-600 mb-6">Bequem von zu Hause aus. Rufen Sie uns an oder fordern Sie einen Rückruf an.</p>
                <Button 
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  onClick={() => setIsCallbackModalOpen(true)}
                >
                  Rückruf anfordern
                </Button>
              </div>
              <div className="bg-slate-50 rounded-2xl p-8 border border-slate-100 hover:shadow-lg transition-shadow text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-6">
                  <Video className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-3">Videoberatung</h3>
                <p className="text-slate-600 mb-6">Persönliche Beratung per Videoanruf – flexibel und modern.</p>
                <Button 
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  onClick={() => setIsBookingModalOpen(true)}
                >
                  Termin buchen
                </Button>
              </div>
              <div className="bg-slate-50 rounded-2xl p-8 border border-slate-100 hover:shadow-lg transition-shadow text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-6">
                  <MapPin className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-3">Vor-Ort-Beratung</h3>
                <p className="text-slate-600 mb-6">Besuchen Sie uns in unserem Büro in Bayreuth für ein persönliches Gespräch.</p>
                <Button 
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  onClick={() => setIsBookingModalOpen(true)}
                >
                  Termin vereinbaren
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-br from-blue-600 to-blue-800">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Bereit für Ihre kostenlose Beratung?
            </h2>
            <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
              Unsere Experten freuen sich auf Ihr Anliegen. Unverbindlich und kostenfrei.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button 
                size="lg" 
                className="bg-white text-blue-600 hover:bg-blue-50"
                onClick={() => setIsBookingModalOpen(true)}
              >
                Beratungstermin buchen
                <Calendar className="w-5 h-5 ml-2" />
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-white text-white hover:bg-white/10"
                onClick={() => setIsCallbackModalOpen(true)}
              >
                <Phone className="w-5 h-5 mr-2" />
                Rückruf anfordern
              </Button>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <ChatWidget />
    </div>
  );
};

export default ConsultationPage;
